import { z } from 'zod';
import { EventListItemDto } from './event';
import { StrengthTraitSlugSchema } from './event-feedback/strength-traits';
import { PublicPersonalityCodeDto } from './personality-code';
import { PublicCardSocialsDto } from './public-card';

export const ClubJoinPolicy = z.enum(['REQUEST', 'INVITE_ONLY']);
export type ClubJoinPolicy = z.infer<typeof ClubJoinPolicy>;

export const ClubMembershipStatus = z.enum(['PENDING', 'ACTIVE', 'REJECTED', 'LEFT', 'BANNED']);
export type ClubMembershipStatus = z.infer<typeof ClubMembershipStatus>;

export const CLUB_NAME_MAX = 60;
export const CLUB_DESCRIPTION_MAX = 2000;
export const CLUB_APPLICATION_NOTE_MAX = 1000;
export const CLUB_WEBSITE_MAX = 2000;

/**
 * Сайт клуба. Только https: адрес попадает в `href` витрины, а `z.url()` без
 * ограничения схемы пропустил бы `javascript:` (тот же довод, что у ссылок визитки).
 */
export const ClubWebsiteUrl = z.url({ protocol: /^https$/ }).max(CLUB_WEBSITE_MAX);

export const ClubDto = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Presigned URL широкого баннера витрины; null = обложки нет. */
  coverUrl: z.string().nullable(),
  /** Presigned URL квадратного логотипа; null = логотипа нет. */
  logoUrl: z.string().nullable(),
  cityId: z.uuid(),
  joinPolicy: ClubJoinPolicy,
  memberCount: z.number().int().nonnegative(),
  isArchived: z.boolean(),
  /** Статус зрителя; null = никогда не подавал заявку. */
  myMembershipStatus: ClubMembershipStatus.nullable(),
  isOwner: z.boolean(),
  /** null — сайт не указан. */
  websiteUrl: z.string().nullable(),
  /** Соцсети в модели визитки: ник, адрес собирает клиент. */
  socials: PublicCardSocialsDto,
});
export type ClubDto = z.infer<typeof ClubDto>;

/**
 * Публичная строка состава клуба — то, что видит на витрине ЛЮБОЙ зритель.
 *
 * Отдельный тип, а не `ClubMemberDto.pick(...)`: у владельческого DTO другое
 * назначение (окно модерации) и другой набор полей, и как только там появится
 * ещё одно служебное поле, `pick` молча утащил бы его на публику. Здесь нет ни
 * `status` (состав — это только ACTIVE, остальные статусы наружу не выходят),
 * ни `applicationNote` (текст заявки адресован владельцу), ни `createdAt`
 * (дата вступления — лишний сигнал о том, кто в клубе новичок).
 */
export const ClubMemberPreviewDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  photoUrl: z.string().nullable(),
  /** true — зритель связан с участником: взаимный матч, его лайк или со-отметка на фото. */
  isConnection: z.boolean(),
  /** Владелец клуба; всегда первый в выдаче. На строке не печатается, нужен для порядка. */
  isOwner: z.boolean(),
  /** Публичный код личности. null — код не собран. */
  personalityCode: PublicPersonalityCodeDto.nullable(),
  /**
   * Качество, которое другие отмечают чаще всего. null — отметок ещё мало,
   * чтобы это было общим мнением, а не одной заметкой.
   */
  seenAs: StrengthTraitSlugSchema.nullable(),
});
export type ClubMemberPreviewDto = z.infer<typeof ClubMemberPreviewDto>;

/** Потолок состава в ответе витрины; истинный размер клуба — `memberCount`. */
export const CLUB_MEMBERS_PREVIEW_MAX = 100;

/** Витрина клуба: карточка + события клуба (видимые зрителю) + состав. */
export const ClubDetailDto = ClubDto.extend({
  upcomingEvents: z.array(EventListItemDto),
  /**
   * Прошедшие события клуба, новые первыми. Витрина — не архив: отдаём тот же
   * потолок, что у апкамингов, без пагинации. Гейт видимости общий с
   * `upcomingEvents`, иначе история утекала бы шире, чем анонсы.
   */
  pastEvents: z.array(EventListItemDto),
  /**
   * ACTIVE-участники, максимум `CLUB_MEMBERS_PREVIEW_MAX`, уже в порядке
   * «владелец → знакомые зрителя → остальные» (ранжирует сервер: срез обязан
   * идти после сортировки, иначе знакомые выпали бы за отсечку). Хвост
   * считается как `memberCount - members.length`.
   */
  members: z.array(ClubMemberPreviewDto),
});
export type ClubDetailDto = z.infer<typeof ClubDetailDto>;

/** Строка состава клуба — заявка или уже принятый участник. */
export const ClubMemberDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  username: z.string().nullable(),
  photoUrl: z.string().nullable(),
  status: ClubMembershipStatus,
  /** Текст заявки; null — вступил без заявки (приглашение) либо не оставил заметку. */
  applicationNote: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type ClubMemberDto = z.infer<typeof ClubMemberDto>;

export const ClubCreateInput = z.object({
  name: z.string().trim().min(1).max(CLUB_NAME_MAX),
  description: z.string().trim().max(CLUB_DESCRIPTION_MAX).optional(),
  cityId: z.uuid(),
  joinPolicy: ClubJoinPolicy.default('REQUEST'),
  websiteUrl: ClubWebsiteUrl.optional(),
  socials: PublicCardSocialsDto.optional(),
});
export type ClubCreateInput = z.infer<typeof ClubCreateInput>;

// cityId не редактируется: клуб живёт в городе, куда его создали (иначе
// пришлось бы пересчитывать местную видимость событий клуба). slug клиенту
// недоступен — тот же порядок, что и на create, генерится сервером.
// `description: null` — осознанное стирание описания. Без него поле можно было
// только переписать, но не очистить: `.partial()` отличает «не прислали» от
// «прислали пусто» лишь через undefined, а пустая строка оставила бы в колонке
// '' вместо NULL. Так же `websiteUrl: null` стирает сайт; соцсети стираются `[]`.
export const ClubUpdateInput = ClubCreateInput.omit({ cityId: true })
  .partial()
  .extend({
    description: z.string().trim().max(CLUB_DESCRIPTION_MAX).nullable().optional(),
    websiteUrl: ClubWebsiteUrl.nullable().optional(),
  });
export type ClubUpdateInput = z.infer<typeof ClubUpdateInput>;

/**
 * Выбор типовой обложки. Каталог живёт в `@upme/view-logic`, здесь только
 * строка-идентификатор: контракты не должны зависеть от фронтовой либы, а
 * существование пресета всё равно проверяет сервер (неизвестный → 400).
 */
export const ClubCoverPresetInput = z.object({
  presetId: z.string().trim().min(1).max(32),
});
export type ClubCoverPresetInput = z.infer<typeof ClubCoverPresetInput>;

export const ClubApplyInput = z.object({
  note: z.string().trim().max(CLUB_APPLICATION_NOTE_MAX).optional(),
});
export type ClubApplyInput = z.infer<typeof ClubApplyInput>;

export const ClubListQuery = z.object({
  // Обязателен: без него каталог по умолчанию не фильтруется по городу вовсе
  // (Prisma читает `cityId: undefined` как «без фильтра») — см. Task 3 fix
  // round 1, находка про полную утечку каталога по всем городам сразу.
  cityId: z.uuid(),
  search: z.string().trim().min(1).max(64).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type ClubListQuery = z.infer<typeof ClubListQuery>;

export const ClubMembersQuery = z.object({
  status: ClubMembershipStatus.optional(),
});
export type ClubMembersQuery = z.infer<typeof ClubMembersQuery>;
